import React, { useCallback, useMemo, useState } from 'react';

import type { FiltrosDashboard } from '../../../../api/contac/contacDashboardApi';
import DashboardFiltroModal from '../components/DashboardFiltroModal';
import FrotaTab from '../components/FrotaTab';
import VisaoGeralTab from '../components/VisaoGeralTab';
import ComoFunciona, { ComoFuncionaSecao } from '../../../../components/como-funciona/ComoFunciona';
import '../../../../components/como-funciona/help-circle-btn.css';
// Casco, cabeçalho e barra de abas são as classes compartilhadas do sistema. Import explícito:
// contar com o CSS carregado por outra tela é acoplamento invisível (BUG-V74).
import '../../../recepcao/pages/Recepcao.css';
import '../../../../shared/ui/dashboard-base.css';

/**
 * Dashboard da Associação (at-86) — visão analítica da carteira, para CONTAC e ADMIN.
 *
 * Nasceu como "Dashboard Oficina" e mudou de casa em 27/08/2026 (decisão do Victor: "dados
 * financeiros devem ser tratados somente no Contac"). O que ele mostra é a carteira INTEIRA da
 * associação; o dashboard da Oficina responde só pelos veículos que passam por lá.
 *
 * ⚠️ AS ABAS SÃO UM REGISTRO, e é isso que faz aba nova entrar sem refatoração: cada uma se
 * declara em `ABAS` com um `disponivel`. As previstas ficam escritas aqui com `disponivel: false`
 * — não renderizam botão, não renderizam conteúdo, e ligá-las é trocar a flag e apontar o
 * `render`.
 *
 * ⚠️ ABA INDISPONÍVEL FICA OCULTA, NÃO VAZIA. Estrear uma aba que abre num "dados em preparação"
 * é prometer número que não existe — e, pior, é a mesma silhueta de uma aba que falhou ao
 * carregar. Enquanto não houver dado, a aba não existe para o usuário; o rodapé diz, em uma
 * linha, o que ainda vem.
 *
 * ⚠️ OS FILTROS VIVEM AQUI, não em cada aba: trocar de aba preserva o recorte, que é o
 * comportamento esperado de um dashboard (e o contrário — cada aba com o seu filtro — faz o
 * usuário comparar números de períodos diferentes sem perceber).
 */

type AbaId = 'VISAO_GERAL' | 'FROTA' | 'SINISTRALIDADE' | 'INADIMPLENCIA';

interface AbaDefinicao {
  id: AbaId;
  rotulo: string;
  /** `false` = prevista, sem dado ainda. Não vira botão nem conteúdo. */
  disponivel: boolean;
}

const ABAS: AbaDefinicao[] = [
  { id: 'VISAO_GERAL', rotulo: 'Visão Geral', disponivel: true },
  { id: 'FROTA', rotulo: 'Frota & Associados', disponivel: true },
  // Previstas, e registradas em `docs/contac/IDEIAS.md` (seção de 27/08/2026). Sinistralidade
  // depende das tabelas de eventos do SGA (sync `contac/at-09`); a inadimplência detalhada
  // (multa, mora, dias vencidos) depende de coleta que hoje não existe.
  { id: 'SINISTRALIDADE', rotulo: 'Sinistralidade', disponivel: false },
  { id: 'INADIMPLENCIA', rotulo: 'Inadimplência', disponivel: false },
];

const ABAS_DISPONIVEIS = ABAS.filter((aba) => aba.disponivel);

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que é este painel',
    conteudo: (
      <>
        <p>
          Uma leitura analítica da <strong>carteira da empresa</strong>: quem são os associados,
          que veículos eles têm e como o dinheiro entra. Serve para decisão de gestão — não é uma
          tela de operação, e nada aqui altera cadastro, veículo ou cobrança.
        </p>
        <p>
          Cada aba tem o seu próprio <strong>Como Funciona</strong>, com a regra por trás de cada
          gráfico. Vale a pena abrir: vários números aqui parecem óbvios e não são.
        </p>
      </>
    ),
  },
  {
    titulo: 'De onde vêm os números',
    conteudo: (
      <>
        <p>
          Da base já sincronizada com o SGA — a mesma que alimenta as telas do Contac. O painel{' '}
          <strong>não consulta o sistema do SGA na hora</strong> em que você abre a tela.
        </p>
        <p>
          Isso é decisão de projeto: a consulta direta ao SGA chega a demorar minutos e falha com
          frequência; um painel apoiado nela ficaria inutilizável. Em troca, o que você vê reflete a
          <strong> última sincronização</strong>, não o instante atual. Para saber quando ela
          ocorreu, use a tela Sincronização da Base.
        </p>
      </>
    ),
  },
  {
    titulo: 'O período e a defasagem de dois meses',
    conteudo: (
      <>
        <p>
          Os dados financeiros ficam disponíveis com <strong>dois meses de defasagem</strong>: em
          julho, o mês mais recente consultável é maio. Sem período escolhido, o painel mostra as{' '}
          <strong>12 competências mais recentes disponíveis</strong>, e o intervalo em uso aparece
          escrito ao lado do botão Filtros.
        </p>
        <p>
          Se você escolher um período fora dessa janela, o sistema recusa a consulta e avisa — em
          vez de devolver um painel zerado, que seria lido como "não houve movimento".
        </p>
      </>
    ),
  },
  {
    titulo: 'Os filtros valem para todas as abas',
    conteudo: (
      <>
        <p>
          O recorte é aplicado em bloco, ao clicar em <strong>Aplicar Filtros</strong>, e{' '}
          <strong>é preservado ao trocar de aba</strong> — assim as abas sempre falam do mesmo
          recorte.
        </p>
        <p>
          Não há resumo dos filtros fora do modal: para conferir ou desfazer o que está valendo,
          reabra <strong>Filtros</strong>, que sempre mostra os valores em vigor e tem o "Limpar
          filtros" dentro.
        </p>
        <p>
          <strong>Regional</strong> e <strong>Categoria</strong> não se aplicam à aba Visão Geral —
          quando você os usa, aquela aba avisa na tela em vez de mostrar um número que parece
          filtrado e não está.
        </p>
      </>
    ),
  },
  {
    titulo: 'Abas que ainda vão aparecer',
    conteudo: (
      <>
        <p>
          Estão no ar as duas abas que os dados de hoje sustentam:{' '}
          <strong>Visão Geral</strong> e <strong>Frota &amp; Associados</strong>.
        </p>
        <p>
          <strong>Sinistralidade</strong> (quanto a associação gasta em reparos para cada real
          arrecadado) depende da coleta dos eventos, que ainda está sendo construída.{' '}
          <strong>Inadimplência</strong> detalhada, com multa, juros e tempo de atraso, depende de
          uma coleta que hoje não é feita.
        </p>
        <p>
          Elas <strong>não aparecem</strong> em vez de aparecerem vazias: uma aba que promete
          número e não entrega é indistinguível de uma aba com defeito.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem enxerga este painel',
    conteudo: (
      <>
        <p>
          Os perfis <strong>Contac</strong> e <strong>Administrador</strong>. O painel reúne valor
          protegido da carteira e arrecadação — informação de gestão que não faz parte da superfície
          dos demais perfis, inclusive o de TI.
        </p>
        <p>
          Os outros perfis do Contac ficam de fora por regra, não por esquecimento:{' '}
          <strong>Relacionamento</strong> nunca vê valores, e <strong>Consultor</strong> e{' '}
          <strong>Regional</strong> só enxergam a própria carteira — enquanto os números daqui somam
          a base inteira.
        </p>
      </>
    ),
  },
];

interface Props {
  sidebarRef?: React.RefObject<HTMLElement>;
}

const DashboardAssociacaoPage: React.FC<Props> = ({ sidebarRef }) => {
  const [abaAtiva, setAbaAtiva] = useState<AbaId>('VISAO_GERAL');
  const [filtros, setFiltros] = useState<FiltrosDashboard>({});
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [opcoes, setOpcoes] = useState<{ bases: string[]; categorias: string[] }>({
    bases: [],
    categorias: [],
  });
  /** Janela efetivamente usada, relatada pela aba que carregou — some quando nada carregou. */
  const [janela, setJanela] = useState<{ dataInicio: string; dataFim: string } | null>(null);

  /**
   * As opções dos selects saem dos DADOS, não de uma lista fixa no código: base e categoria são
   * cadastro do SGA e mudam sem aviso — uma lista fixa oferece filtro que não existe e esconde o
   * que passou a existir. Atualização funcional para o callback ser estável e não relançar carga.
   */
  const registrarOpcoes = useCallback(
    (novas: { bases?: string[]; categorias?: string[] }) => {
      setOpcoes((anterior) => {
        const unir = (atual: string[], entrando?: string[]) =>
          entrando && entrando.length > 0
            ? Array.from(new Set([...atual, ...entrando])).sort()
            : atual;

        const bases = unir(anterior.bases, novas.bases);
        const categorias = unir(anterior.categorias, novas.categorias);
        if (bases === anterior.bases && categorias === anterior.categorias) return anterior;
        return { bases, categorias };
      });
    },
    [],
  );

  const registrarJanela = useCallback((nova: { dataInicio: string; dataFim: string }) => {
    setJanela((anterior) =>
      anterior?.dataInicio === nova.dataInicio && anterior?.dataFim === nova.dataFim
        ? anterior
        : nova,
    );
  }, []);

  const aplicarFiltros = (novos: FiltrosDashboard) => {
    setFiltros(novos);
    setFiltrosAbertos(false);
  };

  const periodoEscrito = useMemo(() => {
    if (!janela) return null;
    const curta = (iso: string) => {
      const [ano, mes, dia] = iso.split('-');
      return `${dia}/${mes}/${ano}`;
    };
    return `${curta(janela.dataInicio)} a ${curta(janela.dataFim)}`;
  }, [janela]);

  const abasFuturas = ABAS.filter((aba) => !aba.disponivel);

  return (
    <div className="ConteDo recepcao-conte-do do-pagina">
      <div className="recepcao-header-row">
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Dashboard da Associação
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      <div className="do-controles">
        <button type="button" className="do-btn-filtros" onClick={() => setFiltrosAbertos(true)}>
          Filtros
        </button>
        {periodoEscrito && (
          <span className="do-periodo">
            Período em uso: {periodoEscrito}
          </span>
        )}
      </div>

      <div className="recepcao-tabs">
        {ABAS_DISPONIVEIS.map((aba) => (
          <button
            key={aba.id}
            type="button"
            className={`recepcao-tab ${abaAtiva === aba.id ? 'active' : ''}`}
            onClick={() => setAbaAtiva(aba.id)}
          >
            {aba.rotulo}
          </button>
        ))}
      </div>

      {/*
        As abas são MONTADAS E DESMONTADAS na troca (e não escondidas com display:none), para a
        aba que volta recarregar com os filtros em vigor. O custo é uma requisição por visita, que
        é aceitável num painel de leitura.
      */}
      {abaAtiva === 'VISAO_GERAL' && (
        <VisaoGeralTab
          filtros={filtros}
          onOpcoes={registrarOpcoes}
          onJanela={registrarJanela}
        />
      )}
      {abaAtiva === 'FROTA' && (
        <FrotaTab filtros={filtros} onOpcoes={registrarOpcoes} onJanela={registrarJanela} />
      )}

      {abasFuturas.length > 0 && (
        <p className="do-abas-futuras">
          Em preparação: {abasFuturas.map((aba) => aba.rotulo).join(', ')}. Essas visões dependem de
          coletas de dados ainda em construção — por isso não aparecem como abas vazias.
        </p>
      )}

      {filtrosAbertos && (
        <DashboardFiltroModal
          filtrosAplicados={filtros}
          basesDisponiveis={opcoes.bases}
          categoriasDisponiveis={opcoes.categorias}
          onAplicar={aplicarFiltros}
          onFechar={() => setFiltrosAbertos(false)}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Dashboard da Associação"
        secoes={helpSections}
      />
    </div>
  );
};

export default DashboardAssociacaoPage;
